import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X, Plus } from "lucide-react";
import StepHeader, { StepTracker } from "./StepHeader";
import PrivacyNoticeStep from "./PrivacyNoticeStep";
import PurposeSelectionStep from "./PurposeSelectionStep";
import ReviewConfirmStep from "./ReviewConfirmStep";
import CompletedStep from "./CompletedStep";
import API_BASE from "./apiBase";
import {
  purposeGroups,
  consentVersion as defaultConsentVersion,
  formatTimestamp,
} from "./data/mockData";

function clonePurposeGroups() {
  return JSON.parse(JSON.stringify(purposeGroups));
}

function normalizeUserEmail(user) {
  return (user?.email || user?.username || "").toString().trim();
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const PHONE_RE = /^[6-9]\d{9}$/;

function isValidPhoneNumber(phone) {
  const value = String(phone || "").trim();
  if (!PHONE_RE.test(value)) return false;

  const repeated = /^(.)\1{9}$/;
  if (repeated.test(value)) return false;

  const digits = value.split("").map(Number);
  const step = digits[1] - digits[0];
  const isSequential = step !== 0 && digits.slice(1).every((digit, index) => digit - digits[index] === step);
  return !isSequential;
}

// Accepts a raw string that may contain multiple emails separated by comma,
// semicolon, space, or newline (e.g. pasted from Excel/Outlook) and returns
// a clean array of individual email strings.
function splitEmails(raw) {
  return String(raw || "")
    .split(/[,;\s\n]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

// function downloadTextReceipt(consentRecord) { 
function getTenantDisplayName(user, fallback = "WHITEHATS TECHNOLOGIES PVT. LTD.") {
  const storedName =
    (typeof window !== "undefined" && window.localStorage)
      ? (window.localStorage.getItem("tenant_name") || window.localStorage.getItem("tenantName") || "")
      : "";

  return (
    user?.tenant?.name ||
    user?.tenant_name ||
    user?.tenantName ||
    user?.organization_name ||
    user?.tenant?.organization_name ||
    storedName ||
    fallback
  );
}

function downloadTextReceipt(consentRecord, tenantName) {
  const emailsLine = Array.isArray(consentRecord.emails)
    ? consentRecord.emails.join(", ")
    : consentRecord.email;

  const lines = [
    tenantName || consentRecord?.tenantName || consentRecord?.tenant_name || "WHITEHATS TECHNOLOGIES PVT. LTD.",
    "Consent Receipt",
    "--------------------------------------",
    `Consent ID: ${consentRecord.consentId}`,
    `Date & Time: ${consentRecord.timestamp}`,
    `Consent Version: ${consentRecord.consentVersion}`,
    `Email(s): ${emailsLine}`,
    "",
    "Mandatory Purposes:",
    ...consentRecord.purposes.mandatory.items
      .filter((i) => i.checked)
      .map((i) => `  - ${i.label}`),
    "",
    "Optional Purposes:",
    ...consentRecord.purposes.optional.items
      .filter((i) => i.checked)
      .map((i) => `  - ${i.label}`),
    "",
    "Third-Party Sharing:",
    ...consentRecord.purposes.thirdParty.items
      .filter((i) => i.checked)
      .map((i) => `  - ${i.label}`),
    "",
    "Data Retention Period: 45 days",
    "",
    "This receipt confirms that consent was recorded for the above purposes.",
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${consentRecord.consentId}-receipt.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function PurposeConsentPage({ user, onMenu, onBack }) {
  const [step, setStep] = useState(1);

  // ---- Bulk email state ----
  // `emails` is the final list that gets submitted. `emailInput` is just the
  // text box value before the user hits "Add" / Enter / comma.
  const [emails, setEmails] = useState(() => {
    const e = normalizeUserEmail(user);
    return e ? [e] : [];
  });
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientNames, setRecipientNames] = useState(() => {
    const e = normalizeUserEmail(user);
    if (e) return { [e]: (user?.name || user?.username || "") };
    return {};
  });
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientDob, setRecipientDob] = useState("");
  const [recipientPhones, setRecipientPhones] = useState(() => {
    const e = normalizeUserEmail(user);
    if (e) return { [e]: (user?.phone || "") };
    return {};
  });
  const [recipientDobs, setRecipientDobs] = useState(() => {
    const e = normalizeUserEmail(user);
    if (e) return { [e]: (user?.dob || "") };
    return {};
  });
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // ---- Dynamic consent version ----
  // Falls back to the static default from mockData until/unless the backend
  // returns the live version for this tenant's active consent bill.
  const [consentVersion, setConsentVersionState] = useState(defaultConsentVersion);

  useEffect(() => {
    setTenantName(getTenantDisplayName(user));
  }, [user]);

  useEffect(() => {
    const tenantId = user?.tenant_id || localStorage.getItem("tenant_id") || null;
    if (!tenantId) return;

    let cancelled = false;
    axios
      .get(`${API_BASE}/user/consent-version`, { params: { tenant_id: tenantId } })
      .then((res) => {
        const v = res.data?.consent_version || res.data?.version;
        if (!cancelled && v) setConsentVersionState(v);
      })
      .catch(() => {
        // Endpoint may not exist yet / network issue — silently keep default.
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const [purposes, setPurposes] = useState(clonePurposeGroups());
  const [declared, setDeclared] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tenantName, setTenantName] = useState(() => getTenantDisplayName(user));
  const [consentRecord, setConsentRecord] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message) => {
    setToast(message);
    window.clearTimeout(window.__purposeConsentToastTimer);
    window.__purposeConsentToastTimer = window.setTimeout(() => setToast(null), 2400);
  }, []);

  const handleTogglePurpose = (groupKey, itemId) => {
    setPurposes((prev) => {
      const group = prev[groupKey];
      if (!group || group.locked) return prev;
      const updatedItems = group.items.map((item) =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      );
      return { ...prev, [groupKey]: { ...group, items: updatedItems } };
    });
  };

  // ---- Email add / remove handlers ----
  const addEmailsFromInput = () => {
    const candidates = splitEmails(emailInput);
    if (candidates.length === 0) return;

    const invalid = candidates.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length > 0) {
      setEmailError(`Invalid email: ${invalid.join(", ")}`);
      return;
    }

    if (!recipientName || !recipientName.trim()) {
      setNameError("Recipient name is required");
      return;
    }

    if (recipientPhone && !isValidPhoneNumber(recipientPhone)) {
      setPhoneError("Phone must be 10 digits, start with 6-9, and not be a repeated or sequential number.");
      return;
    }

    if (!recipientPhone || !recipientPhone.trim()) {
      setPhoneError("Phone number is required");
      return;
    }

    setEmails((prev) => {
      const next = [...prev];
      const nextNames = { ...recipientNames };
      const nextPhones = { ...recipientPhones };
      const nextDobs = { ...recipientDobs };
      candidates.forEach((e) => {
        if (!next.includes(e)) next.push(e);
        // assign provided recipientName to each added email (if any)
        if (recipientName && !nextNames[e]) nextNames[e] = recipientName;
        if (recipientPhone && !nextPhones[e]) nextPhones[e] = recipientPhone;
        if (recipientDob && !nextDobs[e]) nextDobs[e] = recipientDob;
      });
      setRecipientNames(nextNames);
      setRecipientPhones(nextPhones);
      setRecipientDobs(nextDobs);
      return next;
    });
    setEmailInput("");
    setEmailError("");
    setNameError("");
    setPhoneError("");
    setRecipientName("");
    setRecipientPhone("");
    setRecipientDob("");
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmailsFromInput();
    }
  };

  const removeEmail = (email) => {
    setEmails((prev) => prev.filter((e) => e !== email));
    setRecipientNames((prev) => {
      const copy = { ...prev };
      delete copy[email];
      return copy;
    });
    setRecipientPhones((prev) => {
      const copy = { ...prev };
      delete copy[email];
      return copy;
    });
    setRecipientDobs((prev) => {
      const copy = { ...prev };
      delete copy[email];
      return copy;
    });
  };

  const goBack = () => {
    if (step === 1) return;
    setStep((s) => Math.max(1, s - 1));
  };

  const restartWizard = () => {
    setStep(1);
    setPurposes(clonePurposeGroups());
    setDeclared(false);
    setConsentRecord(null);
  };

  const handleSubmitConsent = async () => {
    // Collect emails from input and existing list
    let currentEmails = [...emails];
    let currentNames = { ...recipientNames };
    let currentPhones = { ...recipientPhones };
    let currentDobs = { ...recipientDobs };

    if (emailInput.trim()) {
      const candidates = splitEmails(emailInput);
      const validCandidates = candidates.filter((e) => EMAIL_RE.test(e));
      validCandidates.forEach((e) => {
        if (!currentEmails.includes(e)) currentEmails.push(e);
        if (!currentNames[e] || !currentNames[e].trim()) {
          currentNames[e] = (recipientName && recipientName.trim())
            ? recipientName.trim()
            : (user?.name || user?.username || e.split('@')[0] || 'Consent Recipient');
        }
        if (recipientPhone && !currentPhones[e]) currentPhones[e] = recipientPhone;
        if (recipientDob && !currentDobs[e]) currentDobs[e] = recipientDob;
      });
    }

    if (currentEmails.length === 0) {
      showToast("Please add at least one email address.");
      return;
    }

    const invalid = currentEmails.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length > 0) {
      showToast(`Invalid email address: ${invalid.join(", ")}`);
      return;
    }

    // Ensure all emails have at least a fallback recipient name
    currentEmails.forEach((e) => {
      if (!currentNames[e] || !currentNames[e].trim()) {
        currentNames[e] = user?.name || user?.username || e.split('@')[0] || 'Consent Recipient';
      }
    });

    setEmails(currentEmails);
    setRecipientNames(currentNames);
    setRecipientPhones(currentPhones);
    setRecipientDobs(currentDobs);

    if (!declared) {
      showToast("Please check the declaration checkbox before providing consent.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        tenant_id: user?.tenant_id || localStorage.getItem("tenant_id") || null,
        user_id: user?.id || user?.user_id || null,
        username: currentNames[currentEmails[0]] || user?.name || user?.username || "",
        name: currentNames[currentEmails[0]] || user?.name || user?.username || "",
        phone: currentPhones[currentEmails[0]] || user?.phone || null,
        dob: currentDobs[currentEmails[0]] || null,
        emails: currentEmails,
        recipient_names: currentNames,
        recipient_phones: currentPhones,
        recipient_dobs: currentDobs,
        email: currentEmails[0],
        purposes,
        consent_version: consentVersion,
      };
      const res = await axios.post(`${API_BASE}/user/purpose-consent`, payload);
      const submittedAt = res.data?.submitted_at || new Date().toISOString();
      setConsentRecord({
        consentId: res.data?.consent_id || res.data?.id,
        timestamp: formatTimestamp(new Date(submittedAt)),
        consentVersion: res.data?.consent_version || consentVersion,
        purposes,
        emails: currentEmails,
        email: currentEmails[0],
        emailed: !!res.data?.emailed,
      });
      setStep(4);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong while submitting your consent. Please try again.";
      showToast(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    } else if (typeof onBack === "function") {
      onBack();
    } else if (typeof onMenu === "function") {
      onMenu("consentoverview");
      try {
        window.history.pushState({}, "", "/user/consent-overview");
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (_) { }
    } else {
      window.history.back();
    }
  }, [step, onBack, onMenu]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--gray-50)", marginTop: "-16px", paddingTop: 0 }}>
      <StepHeader currentStep={step} onBack={handleBack} />

      <main style={{ padding: "24px 28px 67px", width: "100%" }}>
        <StepTracker currentStep={step} />
        {step === 1 && (
          <PrivacyNoticeStep
            onContinue={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <PurposeSelectionStep
            purposes={purposes}
            onTogglePurpose={handleTogglePurpose}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <>
            <div
              style={{
                background: "#fff",
                border: "1px solid var(--gray-200)",
                borderRadius: "var(--radius-md)",
                boxShadow: "var(--shadow-sm)",
                padding: "16px 18px",
                marginBottom: 16,
              }}
            >
              <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--gray-700)", marginBottom: 4 }}>
                Receipt Email(s)
              </label>
              <p style={{ fontSize: 12, color: "var(--gray-500)", margin: "0 0 8px" }}>
                Add one or more email addresses to send the consent receipt to. Press
                Enter or comma after each address, or paste a comma-separated list.
              </p>

              <div style={{ display: "flex", gap: 8, maxWidth: 480 }}>
                <input
                  data-tour="receipt-email-input"
                  type="text"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  onKeyDown={handleEmailKeyDown}
                  placeholder="Enter email address and press Enter"
                  style={{
                    flex: 1,
                    border: `1px solid ${emailError ? "var(--red-300, #fca5a5)" : "var(--gray-200)"}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
                <button
                  type="button"
                  data-tour="add-email-btn"
                  onClick={addEmailsFromInput}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    background: "var(--blue-700)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <Plus size={14} />
                  Add
                </button>
              </div>

              <div style={{ display: "flex", gap: 8, maxWidth: 720, marginTop: 8, alignItems: 'flex-start' }}>
                <input
                  type="text"
                  data-tour="recipient-name-input"
                  value={recipientName}
                  onChange={(e) => {
                    setRecipientName(e.target.value);
                    if (nameError) setNameError("");
                  }}
                  placeholder="Recipient full name *"
                  style={{
                    flex: 1,
                    border: `1px solid ${nameError ? "var(--red-300, #fca5a5)" : "var(--gray-200)"}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  data-tour="recipient-phone-input"
                  value={recipientPhone}
                  maxLength={10}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setRecipientPhone(digits);
                    if (phoneError) setPhoneError("");
                  }}
                  placeholder="Phone *"
                  style={{
                    width: 160,
                    border: `1px solid ${phoneError ? "var(--red-300, #fca5a5)" : "var(--gray-200)"}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
                <input
                  type="date"
                  data-tour="recipient-dob-input"
                  value={recipientDob}
                  onChange={(e) => setRecipientDob(e.target.value)}
                  placeholder="DOB*"
                  style={{
                    width: 160,
                    border: `1px solid ${"var(--gray-200)"}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              {nameError && (
                <p style={{ fontSize: 12, color: "#dc2626", margin: "6px 0 0" }}>{nameError}</p>
              )}

              {phoneError && (
                <p style={{ fontSize: 12, color: "#dc2626", margin: "6px 0 0" }}>{phoneError}</p>
              )}

              {emailError && (
                <p style={{ fontSize: 12, color: "#dc2626", margin: "6px 0 0" }}>{emailError}</p>
              )}

              {emails.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                  {emails.map((e) => {
                    const nameFor = recipientNames?.[e] || '';
                    const phoneFor = recipientPhones?.[e] || '';
                    const dobFor = recipientDobs?.[e] || '';
                    return (
                      <span
                        key={e}
                        data-tour="email-chip"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          background: "var(--blue-50)",
                          color: "var(--blue-700)",
                          fontSize: 12.5,
                          fontWeight: 600,
                          padding: "5px 8px 5px 10px",
                          borderRadius: 999,
                        }}
                      >
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700 }}>{nameFor ? `${nameFor}` : ''}</span>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>{e}</span>
                          {phoneFor && <span style={{ fontSize: 11, color: 'var(--gray-600)' }}>{phoneFor}</span>}
                          {dobFor && <span style={{ fontSize: 11, color: 'var(--gray-600)' }}>{dobFor}</span>}
                        </span>
                        <button
                          type="button"
                          data-tour="remove-email-btn"
                          onClick={() => removeEmail(e)}
                          aria-label={`Remove ${e}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--blue-700)",
                            padding: 0,
                          }}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    );
                  })}

                </div>
              )}

              {emails.length > 0 && (
                <p style={{ fontSize: 11.5, color: "var(--gray-500)", margin: "10px 0 0" }}>
                  {emails.length} recipient{emails.length === 1 ? "" : "s"} will receive this consent receipt.
                </p>
              )}
            </div>

            <ReviewConfirmStep
              purposes={purposes}
              declared={declared}
              onDeclare={setDeclared}
              onBack={() => setStep(2)}
              onSubmit={handleSubmitConsent}
              emails={emails}
              recipientNames={recipientNames}
              recipientPhones={recipientPhones}
              recipientDobs={recipientDobs}
              consentVersion={consentVersion}
            />
          </>
        )}

        {step === 4 && consentRecord && (
          <CompletedStep
            consentId={consentRecord.consentId}
            timestamp={consentRecord.timestamp}
            recipientEmail={consentRecord.emails?.join(", ") || consentRecord.email}
            onDownload={() => downloadTextReceipt(consentRecord, tenantName)}
            onViewConsents={() => { if (onMenu) onMenu("consentoverview"); }}
            onGoToDashboard={restartWizard}
          />
        )}

        {submitting && (
          <div
            role="status"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "20px 28px",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--gray-700)",
                boxShadow: "var(--shadow-lg)",
              }}
            >
              Submitting your consent...
            </div>
          </div>
        )}

        {toast && (
          <div
            role="alert"
            data-tour="toast-message"
            style={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              background: "var(--gray-900)",
              color: "#fff",
              padding: "10px 18px",
              borderRadius: 8,
              fontSize: 13,
              boxShadow: "var(--shadow-lg)",
              zIndex: 100,
            }}
          >
            {toast}
          </div>
        )}
      </main>
    </div>
  );
}